import { NextActions } from "@/components/operations/NextActions";
import { WorkflowSummaryCard } from "@/components/operations/WorkflowSummaryCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  useDeleteInvoice,
  useInvoiceById,
  usePaymentsByTarget,
  usePurchaseOrderById,
  useRecordPayment,
  useSalesOrderById,
  useUpdateInvoice,
} from "@/hooks";
import { formatTimestamp } from "@/utils/formatting";
import { getPurchaseOrderTotal, getSalesOrderTotal } from "@/utils/order-total";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  buildInventoryWorkspacePath,
  formatCurrency,
  getInvoiceAttentionDetails,
} from "./operations-shared";

interface InvoiceSheetProps {
  invoiceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InvoiceSheet(props: Readonly<InvoiceSheetProps>) {
  const { invoiceId, open, onOpenChange } = props;
  const invoiceQuery = useInvoiceById(invoiceId);
  const updateInvoiceMutation = useUpdateInvoice();
  const deleteInvoiceMutation = useDeleteInvoice();
  const invoice = invoiceQuery.data;
  const paymentsQuery = usePaymentsByTarget({
    appliesToType: "invoice",
    appliesToId: invoice?.id || "",
  });
  const salesOrderQuery = useSalesOrderById(invoice?.soId || "");
  const purchaseOrderQuery = usePurchaseOrderById(invoice?.poId || "");
  const salesOrder = salesOrderQuery.data;
  const purchaseOrder = purchaseOrderQuery.data;
  const linkedLabel =
    salesOrder?.clientDisplayName ||
    salesOrder?.clientEmail ||
    purchaseOrder?.vendorDisplayName ||
    purchaseOrder?.vendorEmail ||
    "Linked order";
  const financeHref = invoice?.poId
    ? `/operations/finance?tab=payables&invoiceId=${invoice.id}`
    : `/operations/finance?tab=receivables&invoiceId=${invoice?.id || ""}`;
  const recordPaymentMutation = useRecordPayment();
  const [status, setStatus] = useState<
    "draft" | "sent" | "paid" | "void" | "refunded"
  >("draft");
  const [amountDue, setAmountDue] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [dueAt, setDueAt] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState<
    | "paypal_goods_services"
    | "paypal_friends_family"
    | "paypal_goods_services_fees"
    | "bank"
    | "zelle"
    | "card"
    | "crypto"
    | "other"
  >("bank");
  const [paymentReference, setPaymentReference] = useState("");

  useEffect(() => {
    if (invoice) {
      setStatus(invoice.status);
      setAmountDue(String(invoice.amountDue));
      setCurrency(invoice.currency);
      setDueAt(formatTimestamp(invoice.dueAt, "yyyy-MM-dd") || "");
    }
  }, [invoice]);

  const clearedAmount = useMemo(() => {
    return (paymentsQuery.data || [])
      .filter(
        (payment) =>
          payment.status === "cleared" &&
          payment.direction !== "incoming" &&
          // Sourced payments (PayPal sync) are context-only, not counted.
          !payment.source,
      )
      .reduce((sum, payment) => sum + payment.amount, 0);
  }, [paymentsQuery.data]);
  const outstandingAmount = Math.max(
    0,
    (invoice?.amountDue || 0) - clearedAmount,
  );
  const isOverdue = Boolean(
    invoice?.dueAt &&
      new Date(invoice.dueAt).getTime() < Date.now() &&
      invoice.status !== "paid" &&
      invoice.status !== "void" &&
      invoice.status !== "refunded",
  );
  const health = invoice
    ? getInvoiceAttentionDetails({
        status: invoice.status,
        outstandingAmount,
        isOverdue,
      })
    : undefined;

  const handleRecordPayment = async () => {
    if (!invoice || !paymentAmount) return;
    await recordPaymentMutation.mutateAsync({
      appliesToType: "invoice",
      appliesToId: invoice.id,
      amount: Number(paymentAmount),
      method: paymentMethod,
      status: "cleared",
      ...(paymentReference && { referenceId: paymentReference }),
    });
    setPaymentAmount("");
    setPaymentReference("");
  };

  const handleSave = async () => {
    if (!invoice) {
      return;
    }

    await updateInvoiceMutation.mutateAsync({
      invoiceId: invoice.id,
      status,
      amountDue: Number(amountDue),
      currency,
      dueAt: dueAt || undefined,
    });
  };

  const handleDelete = async () => {
    if (!invoice) {
      return;
    }

    await deleteInvoiceMutation.mutateAsync({
      invoiceId: invoice.id,
    });
    onOpenChange(false);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full max-w-full overflow-y-auto p-0 md:max-w-2xl"
      >
        <div className="border-b px-6 py-5">
          <SheetHeader className="text-left">
            <SheetTitle>Invoice Details</SheetTitle>
            <SheetDescription>
              Review receivables or payables status and continue in the linked
              inventory workspace.
            </SheetDescription>
          </SheetHeader>
        </div>

        {!invoice ? (
          <div className="px-6 py-6 text-sm text-muted-foreground">
            {invoiceQuery.isLoading ? "Loading..." : "Invoice not found."}
          </div>
        ) : (
          <div className="space-y-6 px-6 py-6 text-sm">
            <WorkflowSummaryCard
              title={`Invoice ${invoice.id.slice(0, 8)}`}
              description={linkedLabel}
              status={invoice.status}
              health={health?.state}
              healthReason={health?.reason}
              metrics={[
                {
                  label: "Amount Due",
                  value: formatCurrency({ value: invoice.amountDue }),
                },
                {
                  label: "Cleared",
                  value: formatCurrency({ value: clearedAmount }),
                },
                {
                  label: "Outstanding",
                  value: formatCurrency({ value: outstandingAmount }),
                },
                {
                  label: "Due",
                  value: formatTimestamp(invoice.dueAt, "yyyy-MM-dd") || "-",
                },
              ]}
              footer={
                <div className="flex flex-wrap gap-2">
                  <Button asChild size="sm">
                    <Link to={financeHref}>Record Payment</Link>
                  </Button>
                  <Button asChild size="sm" variant="outline">
                    <Link
                      to={buildInventoryWorkspacePath({
                        inventoryId: invoice.inventoryId,
                        purchaseOrderId: invoice.poId,
                        salesOrderId: invoice.soId,
                        invoiceId: invoice.id,
                        focus: invoice.poId
                          ? "purchase-financials"
                          : "sell-financials",
                      })}
                      onClick={() => onOpenChange(false)}
                    >
                      Open Inventory Workspace
                    </Link>
                  </Button>
                </div>
              }
            />

            <NextActions
              actions={[
                {
                  label: "Record payment",
                  description:
                    "Apply a payment and update the outstanding balance.",
                  href: financeHref,
                  variant: outstandingAmount > 0 ? "default" : "outline",
                },
                {
                  label: "Open linked order",
                  description:
                    "Review the originating purchase or sell order inside the inventory workspace.",
                  href: buildInventoryWorkspacePath({
                    inventoryId: invoice.inventoryId,
                    purchaseOrderId: invoice.poId,
                    salesOrderId: invoice.soId,
                    invoiceId: invoice.id,
                    focus: invoice.poId
                      ? "purchase-financials"
                      : "sell-financials",
                  }),
                  variant: "outline",
                },
              ]}
              onNavigate={() => onOpenChange(false)}
            />

            <div className="grid gap-4 lg:grid-cols-2">
              <div className="rounded-xl border p-4 space-y-3">
                <div className="font-medium">Invoice Snapshot</div>
                <div>
                  Issued:{" "}
                  {formatTimestamp(invoice.issuedAt, "yyyy-MM-dd HH:mm") || "-"}
                </div>
                <div>
                  Due:{" "}
                  {formatTimestamp(invoice.dueAt, "yyyy-MM-dd HH:mm") || "-"}
                </div>
                <div>Currency: {invoice.currency}</div>
                <div>
                  Outstanding: {formatCurrency({ value: outstandingAmount })}
                </div>
              </div>

              <div className="rounded-xl border p-4 space-y-3">
                <div className="font-medium">Linked Order</div>
                {salesOrder ? (
                  <>
                    <div>{salesOrder.productName}</div>
                    <div>
                      {salesOrder.clientDisplayName ||
                        salesOrder.clientEmail ||
                        "Unknown client"}
                    </div>
                    <div>
                      Total:{" "}
                      {formatCurrency({
                        value: getSalesOrderTotal({
                          revenue: salesOrder.revenue,
                          taxCost: salesOrder.taxCost,
                          totalAmount: salesOrder.totalAmount,
                        }),
                      })}
                    </div>
                  </>
                ) : null}
                {purchaseOrder ? (
                  <>
                    <div>{purchaseOrder.productName}</div>
                    <div>
                      {purchaseOrder.vendorDisplayName ||
                        purchaseOrder.vendorEmail ||
                        "Unknown vendor"}
                    </div>
                    <div>
                      Total:{" "}
                      {formatCurrency({
                        value: getPurchaseOrderTotal({
                          buyPrice: purchaseOrder.buyPrice,
                          taxCost: purchaseOrder.taxCost,
                          totalAmount: purchaseOrder.totalAmount,
                        }),
                      })}
                    </div>
                  </>
                ) : null}
                {!salesOrder && !purchaseOrder ? (
                  <div className="text-muted-foreground">
                    {salesOrderQuery.isLoading || purchaseOrderQuery.isLoading
                      ? "Loading linked order..."
                      : "Linked order not found."}
                  </div>
                ) : null}
              </div>
            </div>

            <div
              id="invoice-status-section"
              className="rounded-xl border p-4 space-y-3"
            >
              <div className="font-medium">
                Payments ({(paymentsQuery.data || []).length})
              </div>
              {paymentsQuery.isLoading ? (
                <div className="text-muted-foreground">
                  Loading payment history...
                </div>
              ) : (paymentsQuery.data || []).length === 0 ? (
                <div className="text-muted-foreground">
                  No payments recorded yet.
                </div>
              ) : (
                (paymentsQuery.data || []).map((payment) => (
                  <div
                    key={payment.id}
                    className="flex flex-col gap-1 rounded-lg border bg-muted/20 p-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <div className="font-medium">
                        {formatCurrency({ value: payment.amount })} ·{" "}
                        {payment.status}
                      </div>
                      <div className="text-muted-foreground">
                        {payment.method} ·{" "}
                        {formatTimestamp(
                          payment.paidAt || payment.createdAt,
                          "yyyy-MM-dd HH:mm",
                        ) || "-"}
                      </div>
                    </div>
                    <div className="text-muted-foreground">
                      {payment.referenceId || "No reference"}
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Record Payment</div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Amount</Label>
                  <Input
                    type="number"
                    placeholder={`Outstanding: ${formatCurrency({ value: outstandingAmount })}`}
                    value={paymentAmount}
                    onChange={(event) => setPaymentAmount(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Method</Label>
                  <Select
                    value={paymentMethod}
                    onValueChange={(value) => setPaymentMethod(value as Parameters<typeof setPaymentMethod>[0])}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="paypal_goods_services">PayPal - Goods and Services</SelectItem>
                      <SelectItem value="paypal_friends_family">PayPal - Friends and Family</SelectItem>
                      <SelectItem value="paypal_goods_services_fees">PayPal - Fees</SelectItem>
                      <SelectItem value="bank">Bank</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="crypto">Crypto</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Reference (optional)</Label>
                <Input
                  placeholder="Transaction ID, check number, etc."
                  value={paymentReference}
                  onChange={(event) => setPaymentReference(event.target.value)}
                />
              </div>
              <Button
                onClick={handleRecordPayment}
                disabled={!paymentAmount}
                loading={recordPaymentMutation.isPending}
                loadingText="Recording..."
              >
                Record Cleared Payment
              </Button>
            </div>

            <div className="rounded-xl border p-4 space-y-3">
              <div className="font-medium">Edit Invoice</div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Status</Label>
                  <Select
                    value={status}
                    onValueChange={(value) =>
                      setStatus(
                        value as
                          | "draft"
                          | "sent"
                          | "paid"
                          | "void"
                          | "refunded",
                      )
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="draft">Draft</SelectItem>
                      <SelectItem value="sent">Sent</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="void">Void</SelectItem>
                      <SelectItem value="refunded">Refunded</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Currency</Label>
                  <Input
                    value={currency}
                    onChange={(event) => setCurrency(event.target.value)}
                  />
                </div>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Amount Due</Label>
                  <Input
                    value={amountDue}
                    onChange={(event) => setAmountDue(event.target.value)}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Due Date</Label>
                  <Input
                    type="date"
                    value={dueAt}
                    onChange={(event) => setDueAt(event.target.value)}
                  />
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  onClick={handleSave}
                  loading={updateInvoiceMutation.isPending}
                  loadingText="Saving..."
                >
                  Save Invoice
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleDelete}
                  loading={deleteInvoiceMutation.isPending}
                  loadingText="Deleting..."
                >
                  Delete Invoice
                </Button>
              </div>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
